import orderBy from "lodash/orderBy";
import uniqBy from "lodash/uniqBy";

import type { ColumnOption } from "src/formkit/components/formkit-context";
import { getMergedColumnOptions } from "src/formkit/components/mapper-v2/utils";
import type { TemplateInfo } from "src/formkit/components/types";
import { isAudienceTrait } from "src/formkit/components/utils";
import {
  isRelatedColumn,
  isTraitColumn,
  isTransformedColumn,
} from "src/types/visual";
import { flattenOptions } from "src/ui/select";

/**
 * How an inserted value is referenced in Liquid. Row columns are wrapped as
 * `row['name']`; metadata, traits, and merged columns are already complete
 * expressions.
 */
export type TemplateValueKind = "row" | "metadata" | "trait" | "merged";

export type TemplateColumnItem = {
  /** What to pass to `insertTemplateVariable`. A list that hands back only the chosen value finds the item again to read it. */
  kind: Exclude<TemplateValueKind, "metadata">;
  label: string;
  value: string;
  type?: string | null;
  customType?: string | null;
  inlineDescription?: string;
};

/** The Columns tab entries: row columns by label, then traits, then merged columns. */
export function getTemplateColumnItems(
  columns: ColumnOption[],
): TemplateColumnItem[] {
  const flatColumns = flattenOptions(columns);

  // TODO: Properly support merge columns
  // Boosted columns are object-valued and intentionally unsupported.
  const rowColumns = uniqBy(
    flatColumns
      .filter((column) => typeof column.value === "string")
      .map((column) => ({
        kind: "row" as const,
        customType: column.customType,
        label: column.label,
        type: column.type,
        value: String(column.value),
      })),
    "value",
  );

  const traitColumns = flatColumns.flatMap((column) =>
    (isRelatedColumn(column.value) || isTransformedColumn(column.value)) &&
    isTraitColumn(column.value.column)
      ? [
          {
            kind: "trait" as const,
            label: column.label,
            value: `traits['${column.value.column.name}:${column.value.column.traitDefinitionId}']`,
          },
        ]
      : [],
  );
  const audienceTraitColumns = flatColumns.flatMap((column) =>
    typeof column.value === "object" &&
    typeof column.value.type === "string" &&
    isAudienceTrait(column.value.type)
      ? [
          {
            kind: "trait" as const,
            label: column.label,
            value: `trait_enrichments['${column.value.columnAlias}']`,
          },
        ]
      : [],
  );

  const mergedColumns = getMergedColumnOptions(flatColumns).map((column) => ({
    kind: "merged" as const,
    ...column,
  }));

  return [
    ...orderBy(rowColumns, "label"),
    ...uniqBy([...traitColumns, ...audienceTraitColumns], "value"),
    ...mergedColumns,
  ];
}

/** Inserts a column, trait, merged column, or metadata reference into the template. */
export function insertTemplateVariable(
  currentTemplate: string | undefined,
  value: string,
  kind: TemplateValueKind,
): string {
  const isMetadataOrTrait = kind === "metadata" || kind === "trait";
  const isComplete = isMetadataOrTrait || kind === "merged";

  if (!currentTemplate) {
    return isComplete ? `{{ ${value} }}` : `{{ row['${value}'] }}`;
  }

  let template = currentTemplate.trimEnd();
  if (template.endsWith("}}")) {
    return isComplete
      ? `${template} {{ ${value} }}`
      : `${template} {{ row['${value}'] }}`;
  }

  if (
    template.endsWith("row") ||
    template.endsWith("traits") ||
    template.endsWith("merged")
  ) {
    return isComplete
      ? `${template.substring(0, template.length - 3)}${value} }}`
      : `${template}['${value}'] }}`;
  }

  if (template.endsWith("row.") || template.endsWith("traits.")) {
    template = isMetadataOrTrait
      ? `${template.substring(0, template.length - 4)}${value} }}`
      : template.substring(0, template.length - 1);
    return `${template}['${value}'] }}`;
  }

  if (template.endsWith("{{")) {
    return isComplete
      ? `${template} ${value} }}`
      : `${template} row['${value}'] }}`;
  }

  if (template.lastIndexOf("{{") > template.lastIndexOf("}}")) {
    template += " }}";
  }
  return `${template} {{ ${isComplete ? value : `row['${value}']`} }}`;
}

/** Pipes a Liquid filter onto the template's last expression. */
export function insertTemplateFunction(
  currentTemplate: string | undefined,
  {
    name,
    placeholders,
    inputValue,
  }: Pick<TemplateInfo, "name" | "placeholders" | "inputValue">,
): string {
  const filter = placeholders ? `${name} : ${placeholders.join(", ")}` : name;
  const initialParam = inputValue || "row['<field>']";

  if (!currentTemplate) {
    return `{{ ${initialParam} | ${filter} }}`;
  }

  let template = currentTemplate.trimEnd();
  if (template.endsWith("{{")) {
    return `${template} ${initialParam} | ${filter} }}`;
  }
  if (template.endsWith("}}")) {
    template = template.substring(0, template.length - 2);
  }
  template = template.trimEnd();
  if (!template.endsWith("|")) {
    template = `${template} |`;
  }
  return `${template} ${filter} }}`;
}
